import type { ICourtage } from '@/definitions/fondsDefinitions'
import {
  lineChartEnvestorBase,
  lineChartNormalBase
} from '@/components/ChartConfig/LineChartConfig'
import { barChartBase } from '@/components/ChartConfig/BarChartConfig'
import type { ChartDataset } from 'chart.js'
import type { ChartData, DefaultDataPoint } from 'chart.js/dist/types'

export interface CalculatorOptions {
  period: number
  useAgio: boolean
  service: number
  interest: number
  investment: number
  courtage: ICourtage | null
}

/**
 * Calculate cashback and charts
 */
export class Calculator {
  private _barChartConfig: ChartData = barChartBase
  private _lineChartConfig: ChartData | null = null
  private _maxDiscount: number = 0

  constructor(protected data: CalculatorOptions) {
    if (this.data.courtage === null) {
      this._lineChartConfig = {
        labels: [],
        datasets: []
      }
      return
    }
  }

  private resetLineChart() {
    lineChartEnvestorBase.data = []
    lineChartNormalBase.data = []
  }
  private resetBarChart() {
    this._barChartConfig = barChartBase
  }

  calculateBarChartConfig = (courtages: ICourtage[]): void => {
    this.resetBarChart()
    this._barChartConfig = {
      labels: courtages.map((e) => e.bankName) ?? [],
      datasets: [
        {
          data: courtages.map((e) => e.portfolioCommission || 0) ?? []
        }
      ]
    }
  }

  calculate = (): void => {
    this.resetLineChart()
    if (this.data.courtage === null) {
      return
    }

    const agio = (this.data.courtage.agio || 0) / 100
    const spareffekt = Math.max(0, (this.data.courtage.portfolioCommission || 0) - 22.6) / 10000
    const chartLabel = []
    const chartEnvestor: ChartDataset<'line', DefaultDataPoint<'line'>> = lineChartEnvestorBase
    const chartNormal: ChartDataset<'line', DefaultDataPoint<'line'>> = lineChartNormalBase

    const agioDiscount: number = this.data.useAgio ? 0 : 1
    const service = this.data.service / 100
    const interest = this.data.interest / 100
    const laufend = (this.data.courtage.vv || 0) / 100

    chartEnvestor.data.push(this.data.investment)
    chartNormal.data.push(this.data.investment * (1 - agio * (1 - agioDiscount)))
    chartLabel.push(1)

    let max_spareffekt = 0

    for (let i = 1; i < this.data.period; i++) {
      // @ts-ignore
      const envestor: number = chartEnvestor.data[i - 1] * (1 + interest - laufend + spareffekt)
      // @ts-ignore
      const normal_case: number = chartNormal.data[i - 1] * (1 + interest - laufend - service)

      max_spareffekt = envestor - normal_case

      chartEnvestor.data.push(envestor)
      chartNormal.data.push(normal_case)
      chartLabel.push(i + 1)
    }

    this._lineChartConfig = {
      labels: chartLabel,
      datasets: [chartNormal, chartEnvestor]
    } as ChartData
    this._maxDiscount = max_spareffekt
  }

  get barChartConfig(): ChartData {
    return this._barChartConfig
  }
  get maxDiscount(): number {
    return this._maxDiscount
  }
  get lineChartConfig() {
    return this._lineChartConfig
  }
}
